/**
 * diskScan — desktop-only "let Ellie look through my files" helper.
 *
 * The Electron main process owns all filesystem access (the renderer is fully
 * sandboxed). This module wraps the `electronBridge.files` IPC surface, triages
 * the returned file list down to document-like candidates, and uploads the
 * selected ones through the SAME path as a manual upload (`uploadDocument` →
 * presigned S3 PUT → `/documents/create`), so they flow into the existing OCR /
 * extraction / knowledge-base pipeline with zero server-side special-casing.
 *
 * Grants are per-plan: a folder picked from one plan's chat is bound to that
 * plan in the main process, and every read is scope-checked against that plan's
 * granted roots — so one plan can never read another plan's folder.
 */

import { getElectronBridge, isElectron, type FolderGrant, type ScannedFile } from './platform';
import { uploadDocument } from '../store/actions/notes';

/**
 * File types the backend document processor can actually extract text from
 * (see backend document/process worker: pdfjs, DOCX/XLSX parsers, image OCR,
 * Whisper audio, plain text). Anything outside this set is noise we don't want
 * to spend upload bandwidth or KB credits on.
 */
const EXT_MIME: Record<string, string> = {
  pdf:  'application/pdf',
  doc:  'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls:  'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  csv:  'text/csv',
  txt:  'text/plain',
  rtf:  'application/rtf',
  json: 'application/json',
  jpg:  'image/jpeg',
  jpeg: 'image/jpeg',
  png:  'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  gif:  'image/gif',
  tiff: 'image/tiff',
  tif:  'image/tiff',
  mp3:  'audio/mpeg',
  wav:  'audio/wav',
  m4a:  'audio/mp4',
};

/** Files larger than this are skipped — a misfiled video / disk image is never
 *  a document, and base64-inlining a huge file would blow renderer memory. */
export const MAX_SCAN_FILE_BYTES = 25 * 1024 * 1024; // 25 MB

/** True when running on desktop with the folder-access bridge available. */
export function isDiskScanAvailable(): boolean {
  return isElectron && !!getElectronBridge()?.files;
}

/** MIME type for a (dot-less, lowercased) extension, or octet-stream. */
export function mimeForExt(ext: string): string {
  return EXT_MIME[ext.toLowerCase()] ?? 'application/octet-stream';
}

/** True when a scanned file is a document-like candidate worth uploading. */
export function isCandidate(file: ScannedFile): boolean {
  return (
    !!EXT_MIME[file.ext] &&
    file.size > 0 &&
    file.size <= MAX_SCAN_FILE_BYTES
  );
}

/** Open the native folder picker and bind the chosen folder(s) to a plan. */
export async function grantFolder(projectId: string | number): Promise<FolderGrant[]> {
  const bridge = getElectronBridge();
  if (!bridge?.files) return [];
  return bridge.files.grant(projectId);
}

/** Folders currently connected to a plan. */
export async function listGrants(projectId: string | number): Promise<FolderGrant[]> {
  const bridge = getElectronBridge();
  if (!bridge?.files) return [];
  return bridge.files.grants(projectId);
}

/** Revoke one folder grant (or all of a plan's grants when path omitted). */
export async function revokeGrant(
  projectId: string | number,
  folderPath?: string,
): Promise<FolderGrant[]> {
  const bridge = getElectronBridge();
  if (!bridge?.files) return [];
  return bridge.files.revoke(projectId, folderPath);
}

export interface ScanResult {
  /** Every file found across the plan's granted folders. */
  all: ScannedFile[];
  /** The subset Ellie would actually upload (document-like, within size cap). */
  candidates: ScannedFile[];
}

/** Walk a plan's granted folders and split the results into all / candidates. */
export async function scanProject(projectId: string | number): Promise<ScanResult> {
  const bridge = getElectronBridge();
  if (!bridge?.files) return { all: [], candidates: [] };
  const all = await bridge.files.scan(projectId);
  return { all, candidates: all.filter(isCandidate) };
}

export interface UploadProgress {
  /** Files finished (success or failure). */
  done: number;
  /** Total files in this batch. */
  total: number;
  /** Name of the file currently uploading. */
  current: string;
}

export interface UploadSummary {
  uploaded: ScannedFile[];
  failed: { file: ScannedFile; message: string }[];
}

/** Per-file outcome, emitted as each upload completes so a caller can correlate
 *  the new document id to its later Ably processing-completion event. */
export interface UploadFileResult {
  file: ScannedFile;
  success: boolean;
  /** New document id from /documents/create (present when success). */
  documentId?: string | number | null;
  /** 'processing' (will emit an Ably completion later) or 'active' (done now). */
  status?: string | null;
  message?: string;
}

/**
 * Read each selected file's bytes (scope-checked in main) and push it through
 * the standard upload path. Sequential by design — keeps renderer memory flat
 * and avoids hammering the presigned-URL endpoint. `dispatch` is the Redux
 * dispatch from the calling screen. `onFileResult` fires once per file the
 * moment its upload resolves (before the next begins), carrying the new
 * document id + status so the caller can track backend processing via Ably.
 */
export async function uploadScannedFiles(
  projectId: string | number,
  files: ScannedFile[],
  dispatch: any,
  onProgress?: (p: UploadProgress) => void,
  onFileResult?: (r: UploadFileResult) => void,
  onFileStart?: (file: ScannedFile) => void,
): Promise<UploadSummary> {
  const bridge = getElectronBridge();
  const summary: UploadSummary = { uploaded: [], failed: [] };
  if (!bridge?.files) {
    files.forEach((file) => {
      summary.failed.push({ file, message: 'Desktop file access unavailable' });
      onFileResult?.({ file, success: false, message: 'Desktop file access unavailable' });
    });
    return summary;
  }

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    onFileStart?.(file);
    onProgress?.({ done: i, total: files.length, current: file.name });
    try {
      const base64 = await bridge.files.read(projectId, file.path);
      const mime = mimeForExt(file.ext);
      // data: URI is handled by uploadDocument's web/Electron fetch path
      // (shouldUploadViaFetch → fetch(dataUri).blob() → PUT to S3).
      const dataUri = `data:${mime};base64,${base64}`;
      const result = await dispatch(
        uploadDocument(
          projectId,
          { filename: file.name, name: file.name, size: file.size, type: mime, mime },
          dataUri,
          mime,
        ),
      );
      if (result?.success) {
        summary.uploaded.push(file);
        onFileResult?.({
          file,
          success: true,
          documentId: result?.payload?.id ?? null,
          status: result?.payload?.status ?? null,
        });
      } else {
        summary.failed.push({ file, message: result?.message || 'Upload failed' });
        onFileResult?.({ file, success: false, message: result?.message || 'Upload failed' });
      }
    } catch (err: any) {
      summary.failed.push({ file, message: err?.message || 'Could not read file' });
      onFileResult?.({ file, success: false, message: err?.message || 'Could not read file' });
    }
  }

  onProgress?.({ done: files.length, total: files.length, current: '' });
  return summary;
}

/** Human-readable byte size (e.g. "1.4 MB"). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 KB';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
